import { Box, IconButton, InputArea, Text } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { ArrowRight } from '@wix/wix-ui-icons-common';
import classNames from 'classnames';
import { useCallback, useState, KeyboardEvent } from 'react';

import { RelatedQuestionsSlider } from './RelatedQuestionsSlider';
import { SiteSelectorButton } from './SiteSelectorButton';
import { SiteSelectorModal } from './SiteSelectorModal';
import { useOutsideClick } from '../../../../hooks/useOutsideClick';
import { useChatbotMessageSender } from '../../../ArticleRightSideBar/useChatbotMessageSender';

import css from './index.module.scss';

type PinnedChatInputProps = {
  relatedQuestions?: string[];
  hidden?: boolean;
  hideRelatedQuestions?: boolean;
  alwaysFocused?: boolean;
  forceReferral?: string;
  onBeforeSend?: (text: string) => void | Promise<void>;
};

export const PinnedChatInput = ({
  relatedQuestions,
  hidden,
  hideRelatedQuestions,
  alwaysFocused = false,
  forceReferral,
  onBeforeSend,
}: PinnedChatInputProps) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [isSiteSelectorModalOpen, setIsSiteSelectorModalOpen] = useState(false);
  const { sendMessage } = useChatbotMessageSender({});

  const handleSendMessage = async (message?: string) => {
    const text = message ?? query;
    if (!text.trim()) {
      return;
    }
    setQuery('');
    await onBeforeSend?.(text);
    sendMessage(text, forceReferral);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSendMessage();
    }
  };
  const handleOutsidePinnedChatClick = useCallback(() => {
    if (isSiteSelectorModalOpen) {
      return;
    }
    setIsFocused(false);
  }, [isSiteSelectorModalOpen]);
  const outsideClickRef = useOutsideClick(handleOutsidePinnedChatClick);

  const showRelatedQuestions =
    !hideRelatedQuestions &&
    isFocused &&
    relatedQuestions &&
    relatedQuestions.length > 0;

  return (
    <Box
      className={classNames(css.wrapper, {
        [css.hidden]: hidden,
        [css.fitContainer]: alwaysFocused,
      })}
      ref={outsideClickRef}
      direction="vertical"
    >
      {showRelatedQuestions && (
        <RelatedQuestionsSlider
          relatedQuestions={relatedQuestions}
          onQuestionClick={handleSendMessage}
        />
      )}
      <div
        className={classNames(css.inputBox, {
          [css.focused]: isFocused || alwaysFocused,
        })}
        onClick={() => !isFocused && setIsFocused(true)}
      >
        <InputArea
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          value={query}
          rows={isFocused || alwaysFocused ? 2 : 1}
          className={css.inputArea}
          size="medium"
          placeholder={t('article-page.copilot.input.placeholder')}
          autoFocus={isFocused || alwaysFocused}
        />
        <div className={css.inputFooter}>
          <SiteSelectorButton
            onOpenPicker={() => setIsSiteSelectorModalOpen(true)}
          />
          <IconButton
            size="medium"
            className={css.sendButton}
            skin="ai"
            disabled={!query.trim()}
            onClick={() => handleSendMessage()}
          >
            <ArrowRight />
          </IconButton>
        </div>
        {!isFocused && !alwaysFocused && (
          <IconButton
            size="small"
            className={css.sendButton}
            skin="ai"
            disabled
            onClick={() => handleSendMessage()}
          >
            <ArrowRight />
          </IconButton>
        )}
      </div>
      {alwaysFocused && (
        <Box className={css.disclaimer}>
          <Text size="tiny" weight="thin">
            {t('article-page.copilot.input.ai.disclaimer')}
          </Text>
        </Box>
      )}
      <SiteSelectorModal
        isOpen={isSiteSelectorModalOpen}
        onClose={() => setIsSiteSelectorModalOpen(false)}
        isLoadingSites={false}
      />
    </Box>
  );
};
